import { Injectable, Injector } from '@angular/core';
import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AuthService } from './auth.service';

/**
 * Set on an HttpRequest's context (via `new HttpContext().set(EXPIRE_SESSION_ON_401, true)`) to
 * let a 401 on that request end the session.
 *
 * Opt-in, and deliberately so. A 401 only means "the auth cookie is gone" on an endpoint that does
 * nothing but assert the cookie - Authenticate/heartbeat is the one we have. Everywhere else the
 * API also returns 401 for authorization failures: ~25 controller actions answer a failed
 * HasOrganizationAccess with Unauthorized(), and Startup maps UnauthorizedAccessException to 401
 * as well. Those mean "not your row", not "not logged in", and destroying the session over one
 * logged the user out for clicking an inactive org.
 *
 * So a request that does not set this leaves its 401 to the caller, which is where the handling
 * already lives - see OrganizationNavigationGuard, which catches 401/403 and shows
 * Unauthorized.OrganizationNotInScope.
 */
export const EXPIRE_SESSION_ON_401 = new HttpContextToken<boolean>(() => false);

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  // AuthService depends on HttpClient, so injecting it here would create a cyclic dependency
  // while the interceptor chain is being built. Resolve it lazily, once a 401 has happened.
  constructor(private injector: Injector) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      tap({
        error: (err: unknown) => {
          if (
            err instanceof HttpErrorResponse &&
            err.status === 401 &&
            request.context.get(EXPIRE_SESSION_ON_401)
          ) {
            // The auth cookie is gone or past its absolute cap - there is nothing to refresh.
            this.injector.get(AuthService).expireSession();
          }
        },
      })
    );
  }
}

export const authInterceptorProvider = {
  provide: HTTP_INTERCEPTORS,
  useClass: AuthInterceptor,
  multi: true,
};
